import { useState } from 'react';
import toast from 'react-hot-toast';
import { Apple, Download, ExternalLink, Globe, LoaderCircle, Plus, RefreshCw, Smartphone, Star, Trash2, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/Button';
import { jobHunterService, type JhPortfolioItem } from '@/services/job-hunter.service';
import { timeAgo } from '@/lib/job-hunter/format';
import { Pill, TagInput } from './ui';

const KIND = {
  android: { label: 'Android · Google Play', Icon: Smartphone, tone: 'success' as const },
  ios: { label: 'iOS · App Store', Icon: Apple, tone: 'neutral' as const },
  web: { label: 'Website / project', Icon: Globe, tone: 'info' as const },
};

function Item({ profileUid, item, onChange }: { profileUid: string; item: JhPortfolioItem; onChange: (items: JhPortfolioItem[]) => void }) {
  const [role, setRole] = useState(item.role ?? '');
  const [busy, setBusy] = useState<string | null>(null);
  const k = KIND[item.kind];
  const act = async (key: string, fn: () => Promise<JhPortfolioItem[]>, ok?: string) => {
    setBusy(key);
    try { onChange(await fn()); if (ok) toast.success(ok); } catch { /* toasted */ } finally { setBusy(null); }
  };
  return (
    <article className="flex flex-col gap-3 rounded-2xl border border-border bg-surface p-4">
      <div className="flex items-start gap-3">
        {item.iconUrl
          ? <img src={item.iconUrl} alt="" referrerPolicy="no-referrer" className="h-14 w-14 shrink-0 rounded-2xl border border-border object-cover" />
          : <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-primary-soft text-primary"><k.Icon size={22} /></span>}
        <div className="min-w-0 flex-1">
          <a href={item.url} target="_blank" rel="noreferrer" className="line-clamp-2 text-[14.5px] font-semibold leading-snug hover:text-primary">{item.title}</a>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <Pill tone={k.tone} icon={<k.Icon size={11} />}>{k.label}</Pill>
            {item.developer && <span className="text-[11.5px] text-text-muted">by {item.developer}</span>}
          </div>
        </div>
        <div className="flex shrink-0 gap-1">
          <button type="button" title="Fetch the details again" disabled={!!busy} onClick={() => act('refresh', () => jobHunterService.refreshPortfolio(profileUid, item.itemId), 'Details refreshed')} className="grid h-8 w-8 place-items-center rounded-lg border border-border text-text-muted hover:border-primary hover:text-primary disabled:opacity-50">{busy === 'refresh' ? <LoaderCircle size={14} className="animate-spin" /> : <RefreshCw size={14} />}</button>
          <button type="button" title="Remove" disabled={!!busy} onClick={() => { if (window.confirm(`Remove “${item.title}” from the portfolio?`)) void act('remove', () => jobHunterService.removePortfolio(profileUid, item.itemId), 'Removed'); }} className="grid h-8 w-8 place-items-center rounded-lg border border-border text-text-muted hover:border-danger hover:text-danger disabled:opacity-50"><Trash2 size={14} /></button>
        </div>
      </div>
      {(item.rating || item.installs || item.category) && (
        <div className="flex flex-wrap gap-3 text-[12px] font-semibold text-text-secondary">
          {item.rating ? <span className="inline-flex items-center gap-1"><Star size={13} className="fill-warning text-warning" />{item.rating}{item.ratingCount ? <span className="font-normal text-text-muted">({item.ratingCount.toLocaleString()})</span> : null}</span> : null}
          {item.installs && <span className="inline-flex items-center gap-1"><Download size={13} className="text-success" />{item.installs} downloads</span>}
          {item.category && <span className="capitalize text-text-muted">{item.category}</span>}
        </div>
      )}
      {item.description && <p className="line-clamp-3 text-[12.5px] leading-relaxed text-text-secondary">{item.description}</p>}
      {item.screenshots?.length ? (
        <div className="flex gap-2 overflow-x-auto">{item.screenshots.map((s) => <img key={s} src={s} alt="" referrerPolicy="no-referrer" className="h-28 rounded-lg border border-border object-cover" />)}</div>
      ) : null}
      {item.techHints?.length ? <div className="flex flex-wrap gap-1.5">{item.techHints.map((t) => <span key={t} className="jh-chip">{t}</span>)}</div> : null}
      {item.fetchError && <div className="flex items-center gap-1.5 text-[12px] text-warning"><TriangleAlert size={13} />Last refresh failed: {item.fetchError}</div>}
      <div className="grid gap-2 border-t border-border pt-3 @lg:grid-cols-2">
        <label className="block">
          <span className="jh-field-label">Your role</span>
          <input className="jh-input" value={role} placeholder="e.g. Sole developer — Flutter app, FastAPI backend" onChange={(e) => setRole(e.target.value)}
            onBlur={() => { if (role !== (item.role ?? '')) void act('role', () => jobHunterService.updatePortfolio(profileUid, item.itemId, { role }), 'Saved'); }} />
        </label>
        <label className="block">
          <span className="jh-field-label">Tech used</span>
          <TagInput value={item.tech ?? []} onChange={(tech) => void act('tech', () => jobHunterService.updatePortfolio(profileUid, item.itemId, { tech }))} placeholder="Flutter, Firebase…" />
        </label>
      </div>
      <div className="flex items-center justify-between text-[11px] text-text-muted">
        <a href={item.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 truncate hover:text-primary">{item.url}<ExternalLink size={11} /></a>
        <span className="shrink-0">read {timeAgo(item.fetchedAt)}</span>
      </div>
    </article>
  );
}

/** Apps and sites the candidate built: paste a link, the public details fill in, and the AI cites them. */
export function PortfolioEditor({ profileUid, items, onChange }: { profileUid: string; items: JhPortfolioItem[]; onChange: (items: JhPortfolioItem[]) => void }) {
  const [url, setUrl] = useState('');
  const [role, setRole] = useState('');
  const [busy, setBusy] = useState(false);
  const add = async () => {
    setBusy(true);
    try {
      const next = await jobHunterService.addPortfolio(profileUid, { url: url.trim(), role: role.trim() || undefined });
      onChange(next);
      const added = next[next.length - 1];
      toast.success(`Added “${added?.title ?? 'item'}”`);
      setUrl(''); setRole('');
    } catch { /* toasted */ } finally { setBusy(false); }
  };
  const valid = /^https?:\/\/\S+\.\S+/.test(url.trim());
  return (
    <div className="space-y-4 @container">
      <div className="rounded-2xl border border-border bg-surface-alt/50 p-3.5">
        <div className="grid gap-2 @3xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_auto]">
          <input className="jh-input" value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && valid) void add(); }}
            placeholder="https://play.google.com/store/apps/details?id=…  ·  apps.apple.com/…  ·  your website" />
          <input className="jh-input" value={role} onChange={(e) => setRole(e.target.value)} placeholder="Your role (optional)" />
          <Button loading={busy} disabled={!valid} iconLeft={<Plus size={14} />} onClick={add}>Add &amp; fetch</Button>
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5 text-[11.5px] text-text-muted">
          <span className="jh-chip"><Smartphone size={11} />Google Play</span><span className="jh-chip"><Apple size={11} />App Store</span>
          <span className="jh-chip"><Globe size={11} />Website, portfolio or GitHub</span>
          <span>— name, description, icon, rating and downloads are read automatically.</span>
        </div>
      </div>
      {items.length
        ? <div className="grid gap-3 @4xl:grid-cols-2">{items.map((i) => <Item key={i.itemId} profileUid={profileUid} item={i} onChange={onChange} />)}</div>
        : <p className="jh-muted">No portfolio yet. Live apps with real downloads are strong proof — the agent mentions the most relevant one in each cover note.</p>}
    </div>
  );
}
